import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Hoot from "../Hoot";
import { Modal } from "react-bootstrap";
import html2pdf from "html2pdf.js";
import toast from "react-hot-toast";
import {
  FiPlus, FiX, FiFileText, FiDollarSign, FiList,
  FiPackage, FiPercent, FiCheck, FiAlertCircle,
  FiSave, FiDownload, FiChevronDown, FiChevronUp,
  FiRefreshCw, FiSettings, FiTrash2, FiToggleLeft,
  FiToggleRight, FiShield, FiZap, FiGlobe, FiCreditCard,
  FiEye, FiEyeOff, FiEdit3, FiSend, FiInfo, FiChevronRight,
} from "react-icons/fi";
import { PiFilePdf } from "react-icons/pi";
import { PDFDocument } from "pdf-lib";

/* =====================================================================
   CATÁLOGO EDUALL
===================================================================== */
const EDUALL_PRICING = {
  currency: "Kz",
  modules: [
    "Secretaria","Biblioteca","Chat / Gestor de tarefas",
    "Administração","Finanças","Transporte escolar",
    "Portal do Professor","Portal do Aluno",
    "Portal do Encarregado","Pedagogia",
    "Monitoramento / CCTV","Sistemas ERP",
  ],
  categories: {
    universities: {
      name: "Universidades e Institutos Superiores",
      plans: [
        { id:"uni_platina",  name:"Plano Platina",  price:399999, billing:"mensal", includedModules:"todos" },
        { id:"uni_diamante", name:"Plano Diamante", price:469999, billing:"mensal", includedModules:"todos" },
      ],
    },
    publicSchools: {
      name: "Escolas Públicas",
      plans: [
        { id:"pub_basico",   name:"Básico",   price:66999, billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefas","Administração","Finanças","Transporte escolar"] },
        { id:"pub_premium",  name:"Premium",  price:76999, billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefas","Administração","Finanças","Transporte escolar","Portal do Professor","Pedagogia","Monitoramento / CCTV"] },
        { id:"pub_moderado", name:"Moderado", price:2499, billing:"anual_por_aluno", minimumStudents:350, includedModules:"todos" },
      ],
    },
    privateSchools: {
      name: "Colégios & Escolas Privadas",
      plans: [
        { id:"priv_basico",   name:"Básico",   price:99999,  billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefas","Administração","Finanças","Transporte escolar"] },
        { id:"priv_premium",  name:"Premium",  price:169999, billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefas","Administração","Finanças","Transporte escolar","Portal do Professor","Pedagogia"] },
        { id:"priv_avancado", name:"Avançado", price:249999, billing:"mensal", includedModules:"todos" },
      ],
    },
  },
  additionalServices: [
    { id:"sms",               name:"SMS Institucional",                     price:13.99,  billing:"por_sms",       unit:"por SMS" },
    { id:"pagamentos_online", name:"Pagamentos Online",                     price:79999,  billing:"mensal",        unit:"mensal" },
    { id:"manutencao_anual",  name:"Manutenção / Formação / Suporte Anual", price:39999,  billing:"anual_unico",   unit:"anual único" },
    { id:"leitor_rfid",       name:"Leitor RFID Portátil",                  price:25999,  billing:"pagamento_unico", unit:"unidade" },
    { id:"terminal_rfid",     name:"Terminal RFID Standalone",              price:99840,  billing:"pagamento_unico", unit:"unidade" },
  ],
};

const BILLING_MULT   = { trimestral:3, semestral:6, anual:12 };
const BILLING_LABELS = { trimestral:"Trimestral", semestral:"Semestral", anual:"Anual" };

const DEFAULT_CUSTOM_ITEMS = [
  { id:1, name:"Leitor USB para cartões RFID", price:19999, qty:1,
    imageUrl:"https://http2.mlstatic.com/D_721846-MLA105265768777_012026-C.jpg" },
  { id:2, name:"Cartão de identificação e acesso codificado com tecnologia RFID (personalizado)", price:4000, qty:1,
    imageUrl:"https://ik.imagekit.io/zks5iegia/zebra-800059-304.jpg" },
];

const COUNTRY_OPTIONS = [
  { value:"AO", label:"Angola (AO)" },
  { value:"PT", label:"Portugal (PT)" },
  { value:"PT-MA", label:"Portugal — Madeira (PT-MA)" },
  { value:"PT-AC", label:"Portugal — Açores (PT-AC)" },
  { value:"CV", label:"Cabo Verde (CV)" },
  { value:"MZ", label:"Moçambique (MZ)" },
];

const PAYMENT_METHOD_LABELS = [
  "Dinheiro","Transferência Bancária","Depósito",
  "TPA / Multibanco","Cheque","MBWay","PayPal",
  "Crédito","Débito","Voucher","Outro",
];

// ── Tipos de documento Vendus completos ─────────────────────
const VENDUS_DOC_TYPES = {
  FT: { label:"Fatura",                     icon:"🧾", color:"#10b981", bg:"rgba(16,185,129,.1)",  needsPayment:true,  needsItems:true,  canRef:false, desc:"Documento fiscal definitivo" },
  FR: { label:"Fatura-Recibo",              icon:"🗒️", color:"#059669", bg:"rgba(5,150,105,.1)",   needsPayment:true,  needsItems:true,  canRef:false, desc:"Fatura com recibo incorporado" },
  FS: { label:"Fatura Simplificada",        icon:"📄", color:"#0d9488", bg:"rgba(13,148,136,.1)",  needsPayment:true,  needsItems:true,  canRef:false, desc:"Para transações de valor reduzido" },
  FP: { label:"Fatura Pro-Forma",           icon:"📝", color:"#6366f1", bg:"rgba(99,102,241,.1)",  needsPayment:true,  needsItems:true,  canRef:false, desc:"Documento preliminar / orçamento formal" },
  NC: { label:"Nota de Crédito",            icon:"↩️", color:"#f59e0b", bg:"rgba(245,158,11,.1)",  needsPayment:false, needsItems:true,  canRef:true,  desc:"Anula ou reduz valor de fatura" },
  ND: { label:"Nota de Débito",             icon:"↪️", color:"#ef4444", bg:"rgba(239,68,68,.1)",   needsPayment:false, needsItems:true,  canRef:true,  desc:"Acrescenta valor a uma fatura" },
  RC: { label:"Recibo",                     icon:"🧾", color:"#8b5cf6", bg:"rgba(139,92,246,.1)",  needsPayment:true,  needsItems:false, canRef:true,  desc:"Comprovativo de pagamento recebido" },
  OR: { label:"Orçamento",                  icon:"📋", color:"#3b82f6", bg:"rgba(59,130,246,.1)",  needsPayment:false, needsItems:true,  canRef:false, desc:"Proposta de preço sem compromisso" },
  GT: { label:"Guia de Transporte",         icon:"🚚", color:"#64748b", bg:"rgba(100,116,139,.1)", needsPayment:false, needsItems:true,  canRef:false, desc:"Acompanha mercadorias em trânsito" },
  DC: { label:"Doc. Conferência",           icon:"🔍", color:"#94a3b8", bg:"rgba(148,163,184,.1)", needsPayment:false, needsItems:true,  canRef:false, desc:"Consulta / conferência interna" },
};

/* =====================================================================
   HELPERS
===================================================================== */
function genCode() { return `EDPR-${Math.floor(100000000+Math.random()*900000000)}#`; }
let _uid = 200;
const uid = () => ++_uid;
const lineTotal = (it) => (parseFloat(it.price)||0)*(parseInt(it.qty)||1);
function fmtKz(n) { return Number(n||0).toLocaleString("pt-PT"); }
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("pt-PT", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" });
}

// ── PDF blob cache ────────────────────────────────────────────
const _blobCache = new Map();
function cacheGet(id,k){ return _blobCache.get(String(id))?.[k]??null; }
function cacheSet(id,k,url){
  const key=String(id);
  if(!_blobCache.has(key)){
    if(_blobCache.size>=30){
      const o=_blobCache.keys().next().value;
      const v=_blobCache.get(o);
      if(v?.original)URL.revokeObjectURL(v.original);
      if(v?.duplicate)URL.revokeObjectURL(v.duplicate);
      _blobCache.delete(o);
    }
    _blobCache.set(key,{});
  }
  _blobCache.get(key)[k]=url;
}
async function buildDuplicatePdf(docId,origUrl){
  const cached=cacheGet(docId,"duplicate"); if(cached) return cached;
  const resp=await fetch(origUrl); const buf=await resp.arrayBuffer();
  const src=await PDFDocument.load(buf); const out=await PDFDocument.create();
  const A4={width:841.89,height:595.28}; const total=src.getPageCount();
  for(let i=0;i<total;i++){
    const sp=src.getPage(i); const {width:sw,height:sh}=sp.getSize();
    const page=out.addPage([A4.width,A4.height]); const emb=await out.embedPage(sp);
    const hw=A4.width/2; const bs=Math.min(hw/sw,A4.height/sh);
    const dw=sw*bs,dh=sh*bs,y=(A4.height-dh)/2;
    page.drawPage(emb,{x:(hw-dw)/2,y,width:dw,height:dh});
    page.drawPage(emb,{x:hw+(hw-dw)/2,y,width:dw,height:dh});
    await new Promise(r=>setTimeout(r,4));
  }
  const url=URL.createObjectURL(new Blob([await out.save()],{type:"application/pdf"}));
  cacheSet(docId,"duplicate",url); return url;
}
function triggerDl(url,name){
  setTimeout(()=>{
    const a=document.createElement("a");
    a.href=url; a.download=name; a.style.display="none";
    document.body.appendChild(a); a.click();
    requestAnimationFrame(()=>a.remove());
  },0);
}

/* =====================================================================
   CSS
===================================================================== */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');

/* ── Modal shell ── */
.sp-modal .modal-dialog { max-width:min(980px,97vw); }
.sp-modal .modal-content {
  border-radius:20px;
  border:1px solid rgba(226,232,240,.95);
  box-shadow:0 32px 100px rgba(2,6,23,.24);
  overflow:hidden;
  font-family:'Montserrat',sans-serif;
}
.sp-modal .modal-body { padding:0; }

/* ── Layout ── */
.sp-layout { display:flex; height:min(88vh,780px); }
.sp-sidebar {
  width:216px; flex-shrink:0;
  border-right:1px solid var(--border,#e2e8f0);
  display:flex; flex-direction:column;
  background:#f8f9ff;
  padding:16px 10px;
  gap:2px;
}
.sp-sidebar-logo {
  padding:4px 8px 14px;
  border-bottom:1px solid var(--border,#e2e8f0);
  margin-bottom:8px;
  font-size:.78rem; font-weight:900;
  color:var(--primary,#6052ff);
  letter-spacing:.03em;
  display:flex; align-items:center; gap:7px;
}
.sp-nav {
  border-radius:11px; padding:8px 12px;
  cursor:pointer; display:flex; align-items:center; gap:9px;
  font-size:.79rem; font-weight:700;
  color:var(--text,#0f172a);
  transition:background .12s,color .12s;
  border:none; background:transparent; width:100%; text-align:left;
}
.sp-nav:hover { background:rgba(148,163,184,.10); }
.sp-nav.active { background:var(--primary-soft,#ebe9ff); color:var(--primary,#6052ff); }
.sp-nav-badge {
  margin-left:auto;
  background:var(--primary,#6052ff); color:#fff;
  border-radius:999px; font-size:.6rem; font-weight:900;
  padding:1px 7px; flex-shrink:0;
}
.sp-nav-badge.warn { background:#f59e0b; }
.sp-nav-badge.ok   { background:#10b981; }

/* ── Content area ── */
.sp-content { flex:1; min-width:0; display:flex; flex-direction:column; }
.sp-topbar {
  padding:15px 20px 12px;
  border-bottom:1px solid var(--border,#e2e8f0);
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  flex-shrink:0; background:#fff;
}
.sp-body {
  flex:1; overflow-y:auto;
  padding:16px 20px 28px;
  background:#fff;
}
.sp-body::-webkit-scrollbar { width:4px; }
.sp-body::-webkit-scrollbar-thumb { background:#d1d5db; border-radius:999px; }

/* ── Sections & cards ── */
.sp-section { margin-bottom:18px; }
.sp-section-title {
  font-size:.69rem; font-weight:900;
  text-transform:uppercase; letter-spacing:.08em;
  color:var(--muted,#6b7280);
  margin-bottom:10px;
  display:flex; align-items:center; gap:6px;
}
.sp-card {
  border:1px solid var(--border,#e2e8f0);
  border-radius:14px; padding:14px;
  margin-bottom:12px; background:#fff;
}

/* ── Form elements ── */
.sp-label { font-size:.72rem; font-weight:800; color:var(--muted,#6b7280); margin-bottom:4px; }
.sp-input {
  width:100%;
  border:1px solid rgba(148,163,184,.35);
  border-radius:10px; padding:8px 11px;
  font-size:.84rem; font-weight:600;
  background:#fff; color:var(--text,#0f172a);
  font-family:'Montserrat',sans-serif;
  outline:none; transition:border-color .12s;
}
.sp-input:focus { border-color:var(--primary,#6052ff); }
.sp-select {
  width:100%;
  border:1px solid rgba(148,163,184,.35);
  border-radius:10px; padding:8px 11px;
  font-size:.84rem; font-weight:600;
  background:#fff; color:var(--text,#0f172a);
  font-family:'Montserrat',sans-serif;
  appearance:none; cursor:pointer; outline:none;
}
.sp-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.sp-grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; }
@media(max-width:540px){ .sp-grid-2,.sp-grid-3 { grid-template-columns:1fr; } }

/* ── Buttons ── */
.sp-btn {
  border-radius:999px;
  border:1px solid rgba(148,163,184,.35);
  background:#fff; padding:8px 16px;
  font-size:.81rem; font-weight:800;
  cursor:pointer; display:inline-flex; align-items:center; gap:6px;
  transition:background .12s, opacity .12s;
  font-family:'Montserrat',sans-serif;
}
.sp-btn:hover:not(:disabled) { background:rgba(148,163,184,.10); }
.sp-btn.primary {
  background:var(--primary,#6052ff); color:#fff;
  border-color:var(--primary,#6052ff);
}
.sp-btn.primary:hover:not(:disabled) { background:#4e42e0; }
.sp-btn.danger { background:#ef4444; color:#fff; border-color:#ef4444; }
.sp-btn.danger:hover:not(:disabled) { background:#dc2626; }
.sp-btn.success { background:#10b981; color:#fff; border-color:#10b981; }
.sp-btn.success:hover:not(:disabled) { background:#059669; }
.sp-btn.sm { padding:5px 12px; font-size:.75rem; }
.sp-btn:disabled { opacity:.42; cursor:not-allowed; }
.sp-icon-btn {
  width:32px; height:32px; border-radius:10px;
  border:1px solid rgba(148,163,184,.3);
  background:rgba(148,163,184,.07);
  display:grid; place-items:center;
  cursor:pointer; color:var(--text,#0f172a);
  transition:background .12s; flex-shrink:0;
}
.sp-icon-btn:hover { background:rgba(96,82,255,.10); border-color:rgba(96,82,255,.28); }
.sp-icon-btn:disabled { opacity:.4; cursor:not-allowed; }

/* ── Plan cards ── */
.sp-plan-card {
  border:2px solid var(--border,#e2e8f0);
  border-radius:12px; padding:12px 14px;
  cursor:pointer;
  transition:border-color .12s,background .12s;
  margin-bottom:8px;
}
.sp-plan-card:hover { border-color:rgba(96,82,255,.35); }
.sp-plan-card.sel { border-color:var(--primary,#6052ff); background:rgba(96,82,255,.05); }
.sp-plan-name  { font-size:.86rem; font-weight:900; }
.sp-plan-price { font-size:.79rem; color:var(--muted,#6b7280); font-weight:600; margin-top:3px; }
.sp-plan-check {
  width:18px; height:18px; border-radius:50%;
  border:2px solid rgba(148,163,184,.4);
  display:grid; place-items:center; flex-shrink:0;
  transition:background .12s,border-color .12s;
}
.sp-plan-card.sel .sp-plan-check { background:var(--primary,#6052ff); border-color:var(--primary,#6052ff); }

/* ── Service toggles ── */
.sp-svc-row {
  display:flex; align-items:center; gap:10px;
  padding:9px 0; border-bottom:1px solid rgba(148,163,184,.12);
}
.sp-svc-row:last-child { border-bottom:none; }
.sp-svc-name  { flex:1; font-size:.82rem; font-weight:750; }
.sp-svc-price { font-size:.77rem; color:var(--muted,#6b7280); font-weight:550; }
.sp-toggle { position:relative; width:38px; height:20px; flex-shrink:0; cursor:pointer; }
.sp-toggle input { opacity:0; width:0; height:0; position:absolute; }
.sp-toggle-track {
  position:absolute; inset:0; border-radius:999px;
  background:rgba(148,163,184,.3); transition:background .15s;
}
.sp-toggle input:checked + .sp-toggle-track { background:var(--primary,#6052ff); }
.sp-toggle-thumb {
  position:absolute; width:14px; height:14px;
  background:#fff; border-radius:50%;
  top:3px; left:3px; transition:transform .15s;
  box-shadow:0 1px 3px rgba(0,0,0,.2);
}
.sp-toggle input:checked ~ .sp-toggle-thumb { transform:translateX(18px); }

/* ── Custom product card ── */
.cpc {
  display:grid; grid-template-columns:60px 1fr 28px;
  gap:10px; align-items:start;
  border:1px solid var(--border,#e2e8f0);
  border-radius:12px; padding:12px;
  margin-bottom:10px; background:#fafbff;
}
.cpc-img {
  width:60px; height:60px; border-radius:9px;
  overflow:hidden; border:1px solid #e2e8f0;
  background:#e8eaf0; display:flex;
  align-items:center; justify-content:center;
  font-size:20px; color:#94a3b8; flex-shrink:0;
}
.cpc-img img { width:100%; height:100%; object-fit:cover; }
.cpc-body   { display:flex; flex-direction:column; gap:7px; }
.cpc-sub    { display:grid; grid-template-columns:1fr 80px 62px; gap:6px; align-items:end; }
.cpc-lbl    { font-size:10px; font-weight:700; color:#64748b; margin-bottom:3px; display:block; }
.cpc-badge  { font-size:11px; font-weight:800; color:#4338ca; background:#eef2ff; border-radius:999px; padding:3px 10px; white-space:nowrap; }
.cpc-del    { background:none; border:none; padding:2px; color:#cbd5e1; font-size:15px; cursor:pointer; transition:color .15s; }
.cpc-del:hover { color:#ef4444; }
.add-item-btn {
  width:100%; border:1.5px dashed #a5b4fc;
  background:transparent; color:#6366f1;
  border-radius:10px; padding:9px; font-size:.79rem; font-weight:750;
  cursor:pointer; transition:background .15s;
  display:flex; align-items:center; justify-content:center; gap:6px; margin-top:4px;
  font-family:'Montserrat',sans-serif;
}
.add-item-btn:hover { background:#eef2ff; }

/* ── Module tags ── */
.sp-modules { display:flex; flex-wrap:wrap; gap:6px; }
.sp-module-tag {
  font-size:.71rem; font-weight:750; padding:3px 10px;
  border-radius:999px;
  background:var(--primary-soft,#ebe9ff);
  color:var(--primary,#6052ff);
  border:1px solid var(--primary-border,#d1ceff);
}

/* ── Summary ── */
.sp-summary { border:1px solid var(--border,#e2e8f0); border-radius:14px; overflow:hidden; }
.sp-sum-row {
  display:flex; justify-content:space-between; align-items:center;
  padding:10px 14px; border-bottom:1px solid rgba(148,163,184,.12);
  font-size:.83rem; font-weight:650;
}
.sp-sum-row:last-child { border-bottom:none; }
.sp-sum-row.total { background:var(--primary,#6052ff); color:#fff; font-size:.94rem; font-weight:950; }
.sp-sum-row.discount { color:#ef4444; }
.sp-sum-val { font-weight:950; font-variant-numeric:tabular-nums; white-space:nowrap; }

/* ── Spinner ── */
.sp-spinner {
  display:inline-block; width:13px; height:13px;
  border:2px solid rgba(96,82,255,.2);
  border-top-color:var(--primary,#6052ff);
  border-radius:50%; animation:sp-spin .7s linear infinite;
}
@keyframes sp-spin { to { transform:rotate(360deg); } }

/* ── Empty state ── */
.sp-empty { text-align:center; padding:36px 0; color:var(--muted,#6b7280); font-size:.83rem; }

/* ── Footer ── */
.sp-footer {
  padding:12px 20px; border-top:1px solid var(--border,#e2e8f0);
  display:flex; justify-content:flex-end; gap:8px;
  background:#fff; flex-shrink:0;
}

/* ── Vendus Tab: config card ── */
.vnd-config-card {
  border:1px solid var(--border,#e2e8f0);
  border-radius:16px; overflow:hidden; margin-bottom:16px;
}
.vnd-config-header {
  padding:13px 16px;
  background:linear-gradient(135deg,rgba(96,82,255,.07),rgba(96,82,255,.02));
  border-bottom:1px solid var(--border,#e2e8f0);
  display:flex; align-items:center; gap:10px;
}
.vnd-config-header-icon {
  width:34px; height:34px; border-radius:10px;
  background:var(--primary-soft,#ebe9ff);
  display:grid; place-items:center;
  color:var(--primary,#6052ff); font-size:.95rem; flex-shrink:0;
}
.vnd-config-header-title  { font-size:.86rem; font-weight:900; }
.vnd-config-header-sub    { font-size:.71rem; color:var(--muted,#6b7280); font-weight:600; }
.vnd-config-body          { padding:16px; display:flex; flex-direction:column; gap:12px; }

/* status pills */
.vnd-status {
  display:inline-flex; align-items:center; gap:5px;
  font-size:.7rem; font-weight:800; padding:3px 10px;
  border-radius:999px;
}
.vnd-status.ok   { background:rgba(16,185,129,.1); color:#065f46; border:1px solid rgba(16,185,129,.25); }
.vnd-status.warn { background:rgba(245,158,11,.1); color:#92400e; border:1px solid rgba(245,158,11,.25); }
.vnd-status.err  { background:rgba(239,68,68,.1);  color:#991b1b; border:1px solid rgba(239,68,68,.25); }

/* Payment method rows */
.pm-row {
  display:flex; align-items:center; gap:10px;
  padding:10px 12px;
  border:1px solid var(--border,#e2e8f0);
  border-radius:11px; margin-bottom:8px;
  background:#fff;
}
.pm-row-default {
  border-color:rgba(96,82,255,.3);
  background:rgba(96,82,255,.03);
}
.pm-badge-default {
  font-size:.62rem; font-weight:900;
  background:var(--primary,#6052ff); color:#fff;
  border-radius:999px; padding:2px 8px;
}
.pm-icon {
  width:32px; height:32px; border-radius:9px;
  background:rgba(96,82,255,.08);
  display:grid; place-items:center;
  color:var(--primary,#6052ff); flex-shrink:0;
}
.pm-add-form {
  border:1.5px dashed rgba(96,82,255,.3);
  border-radius:12px; padding:14px;
  background:rgba(96,82,255,.02); margin-top:8px;
}

/* api key input eye */
.sp-input-wrap { position:relative; }
.sp-input-wrap .sp-input { padding-right:40px; }
.sp-input-eye {
  position:absolute; right:10px; top:50%;
  transform:translateY(-50%);
  background:none; border:none;
  color:var(--muted,#6b7280); cursor:pointer;
  display:grid; place-items:center;
  padding:2px;
}

/* divider */
.sp-divider { height:1px; background:rgba(148,163,184,.15); margin:14px 0; }

/* alert box */
.sp-alert {
  display:flex; align-items:flex-start; gap:9px;
  padding:11px 13px; border-radius:11px;
  font-size:.79rem; font-weight:650; margin-bottom:12px;
}
.sp-alert.warn { background:rgba(245,158,11,.08); border:1px solid rgba(245,158,11,.25); color:#92400e; }
.sp-alert.info { background:rgba(96,82,255,.06); border:1px solid rgba(96,82,255,.2); color:#3730a3; }
.sp-alert.ok   { background:rgba(16,185,129,.07); border:1px solid rgba(16,185,129,.22); color:#065f46; }
.sp-alert.err  { background:rgba(239,68,68,.06); border:1px solid rgba(239,68,68,.22); color:#991b1b; }

/* sidebar saved indicator */
.sp-saved-pill {
  font-size:.69rem; padding:5px 9px; border-radius:10px;
  background:rgba(16,185,129,.08); border:1px solid rgba(16,185,129,.2);
  color:#065f46; font-weight:750; display:flex; align-items:center; gap:5px;
}

/* ════════════════════════════════════════════════
   DOCS TAB — completely reworked
════════════════════════════════════════════════ */

/* Doc type selector grid */
.dt-grid {
  display:grid;
  grid-template-columns:repeat(5,1fr);
  gap:6px;
  margin-bottom:14px;
}
@media(max-width:620px){ .dt-grid { grid-template-columns:repeat(3,1fr); } }

.dt-tile {
  border:2px solid var(--border,#e2e8f0);
  border-radius:11px; padding:9px 7px;
  cursor:pointer; text-align:center;
  transition:border-color .12s,background .12s,transform .1s;
  background:#fff;
}
.dt-tile:hover { transform:translateY(-1px); border-color:rgba(96,82,255,.35); }
.dt-tile.sel {
  border-color:var(--sel-color,#6052ff);
  background:var(--sel-bg,rgba(96,82,255,.06));
  transform:translateY(-1px);
}
.dt-tile-icon { font-size:1.3rem; line-height:1; margin-bottom:4px; }
.dt-tile-code { font-size:.65rem; font-weight:900; letter-spacing:.05em; }
.dt-tile-label { font-size:.62rem; font-weight:600; color:var(--muted,#6b7280); margin-top:1px; line-height:1.3; }

/* Doc creation form */
.dc-form {
  border:1px solid var(--border,#e2e8f0);
  border-radius:14px; overflow:hidden;
  margin-bottom:14px; animation:fadeSlide .18s ease;
}
@keyframes fadeSlide { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
.dc-form-header {
  padding:11px 14px;
  border-bottom:1px solid var(--border,#e2e8f0);
  display:flex; align-items:center; gap:10px;
  font-size:.82rem; font-weight:900;
}
.dc-form-body { padding:14px; display:flex; flex-direction:column; gap:12px; }

/* Items table inside doc form */
.items-table { width:100%; border-collapse:collapse; }
.items-table th {
  font-size:.64rem; font-weight:900; color:var(--muted,#6b7280);
  text-transform:uppercase; letter-spacing:.05em;
  padding:6px 8px; border-bottom:1px solid var(--border,#e2e8f0);
  text-align:left; background:#f8f9ff;
}
.items-table td { padding:6px 8px; border-bottom:1px solid rgba(148,163,184,.1); vertical-align:middle; }
.items-table tr:last-child td { border-bottom:none; }
.items-table input.sp-input { padding:5px 8px; font-size:.78rem; }
.items-table .del-btn {
  background:none; border:none; color:#cbd5e1; cursor:pointer; padding:3px; font-size:14px;
  transition:color .15s;
}
.items-table .del-btn:hover { color:#ef4444; }

/* Documents listing table */
.docs-table-wrap {
  border:1px solid var(--border,#e2e8f0);
  border-radius:14px; overflow:hidden;
}
.docs-table { width:100%; border-collapse:collapse; }
.docs-table thead tr { background:#f8f9ff; }
.docs-table th {
  font-size:.64rem; font-weight:900; color:var(--muted,#6b7280);
  text-transform:uppercase; letter-spacing:.05em;
  padding:9px 12px; border-bottom:1px solid var(--border,#e2e8f0);
  text-align:left; white-space:nowrap;
}
.docs-table td { padding:10px 12px; border-bottom:1px solid rgba(148,163,184,.08); font-size:.8rem; font-weight:650; vertical-align:middle; }
.docs-table tbody tr:last-child td { border-bottom:none; }
.docs-table tbody tr:hover { background:rgba(148,163,184,.04); }

/* doc type badge */
.doc-type-badge {
  display:inline-flex; align-items:center; gap:5px;
  font-size:.67rem; font-weight:900;
  padding:3px 9px; border-radius:999px;
}

/* dl actions inside table */
.dl-actions { display:flex; gap:5px; align-items:center; }

/* error detail box */
.err-detail {
  margin-top:10px; padding:10px 12px;
  border-radius:10px; background:rgba(239,68,68,.04);
  border:1px solid rgba(239,68,68,.18);
}
.err-detail pre {
  font-size:.68rem; white-space:pre-wrap; word-break:break-all;
  color:#991b1b; margin:0; max-height:120px; overflow-y:auto;
}

/* ref doc input */
.ref-doc-box {
  border:1.5px dashed rgba(245,158,11,.4);
  border-radius:10px; padding:10px 12px;
  background:rgba(245,158,11,.03);
}
`;

/* =====================================================================
   SUB-COMPONENTS
===================================================================== */

const CustomProductCard = React.memo(function CustomProductCard({ item, onChange, onRemove }) {
  const [imgErr, setImgErr] = useState(false);
  const set = useCallback((f,v) => onChange(item.id,f,v), [item.id, onChange]);
  return (
    <div className="cpc">
      <div className="cpc-img">
        {item.imageUrl && !imgErr
          ? <img src={item.imageUrl} alt="" onError={() => setImgErr(true)}/>
          : <span>🖼</span>}
      </div>
      <div className="cpc-body">
        <div>
          <span className="cpc-lbl">Nome do Produto / Serviço</span>
          <input className="sp-input" placeholder="Ex: Câmera IP Hikvision" value={item.name}
            onChange={e => set("name", e.target.value)}/>
        </div>
        <div className="cpc-sub">
          <div>
            <span className="cpc-lbl">Preço Unit. (Kz)</span>
            <input className="sp-input" type="number" min="0" value={item.price}
              onChange={e => set("price", e.target.value)}/>
          </div>
          <div>
            <span className="cpc-lbl">Qtd.</span>
            <input className="sp-input" type="number" min="1" value={item.qty}
              onChange={e => set("qty", e.target.value)}/>
          </div>
          <div style={{display:"flex",alignItems:"flex-end",paddingBottom:1}}>
            <span className="cpc-badge">{fmtKz(lineTotal(item))} Kz</span>
          </div>
        </div>
        <div>
          <span className="cpc-lbl">URL da Imagem (opcional)</span>
          <input className="sp-input" type="url" placeholder="https://..." value={item.imageUrl}
            onChange={e => { setImgErr(false); set("imageUrl", e.target.value); }}/>
        </div>
      </div>
      <button className="cpc-del" onClick={() => onRemove(item.id)}>✕</button>
    </div>
  );
});

function Toggle({ checked, onChange }) {
  return (
    <label className="sp-toggle">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}/>
      <span className="sp-toggle-track"/>
      <span className="sp-toggle-thumb"/>
    </label>
  );
}

/* ── DocTypeTile ── */
function DocTypeTile({ code, info, selected, onSelect }) {
  return (
    <div
      className={`dt-tile ${selected?"sel":""}`}
      style={{ "--sel-color": info.color, "--sel-bg": info.bg }}
      onClick={() => onSelect(code)}
      title={info.desc}
    >
      <div className="dt-tile-icon">{info.icon}</div>
      <div className="dt-tile-code" style={{color: selected ? info.color : "var(--text,#0f172a)"}}>{code}</div>
      <div className="dt-tile-label">{info.label}</div>
    </div>
  );
}

/* ── ItemsEditor: inline table of items for a doc ── */
function ItemsEditor({ items, onChange }) {
  const add = () => onChange([...items, { id: uid(), title:"", price:0, quantity:1, discount:0, tax:false }]);
  const upd = (id, f, v) => onChange(items.map(it => it.id===id ? {...it,[f]:v} : it));
  const del = (id) => onChange(items.filter(it => it.id!==id));

  return (
    <div>
      <div className="sp-label" style={{marginBottom:6}}>Artigos / Serviços</div>
      {items.length > 0 && (
        <div style={{overflowX:"auto",marginBottom:6}}>
          <table className="items-table" style={{minWidth:520}}>
            <thead>
              <tr>
                <th style={{width:"40%"}}>Descrição</th>
                <th>Preço (Kz)</th>
                <th>Qtd.</th>
                <th>Desc.%</th>
                <th>IVA</th>
                <th style={{width:28}}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id}>
                  <td><input className="sp-input" value={it.title} placeholder="Descrição do artigo" onChange={e=>upd(it.id,"title",e.target.value)}/></td>
                  <td><input className="sp-input" type="number" min="0" value={it.price} onChange={e=>upd(it.id,"price",e.target.value)} style={{minWidth:90}}/></td>
                  <td><input className="sp-input" type="number" min="1" value={it.quantity} onChange={e=>upd(it.id,"quantity",e.target.value)} style={{minWidth:55}}/></td>
                  <td><input className="sp-input" type="number" min="0" max="100" value={it.discount} onChange={e=>upd(it.id,"discount",e.target.value)} style={{minWidth:55}}/></td>
                  <td>
                    <Toggle checked={!!it.tax} onChange={v=>upd(it.id,"tax",v)}/>
                  </td>
                  <td><button className="del-btn" onClick={()=>del(it.id)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button className="add-item-btn" onClick={add} style={{marginTop:4}}>
        <FiPlus style={{fontSize:13}}/> Adicionar artigo
      </button>
    </div>
  );
}

/* ── DocTypeBadge ── */
function DocTypeBadge({ type }) {
  const info = VENDUS_DOC_TYPES[type] || { label: type, icon:"📄", color:"#6b7280", bg:"rgba(107,114,128,.1)" };
  return (
    <span className="doc-type-badge" style={{color:info.color, background:info.bg}}>
      {info.icon} {type}
    </span>
  );
}

/* ── DownloadActions inside the table ── */
function DownloadActions({ doc, dlLoading, onDownload }) {
  const key = doc.id;
  const dl  = dlLoading[key];
  if (doc.type === "proposta") {
    return (
      <button className="sp-btn sm" disabled={!!dl} onClick={() => onDownload(doc, "proposta")} title="Baixar PDF">
        {dl ? <span className="sp-spinner"/> : <><FiDownload style={{fontSize:12}}/> PDF</>}
      </button>
    );
  }
  return (
    <div className="dl-actions">
      <button className="sp-btn sm" disabled={!!dl} onClick={() => onDownload(doc, "original")} title="Original">
        {dl==="original" ? <span className="sp-spinner"/> : <><PiFilePdf style={{fontSize:13,color:"#ef4444"}}/> Original</>}
      </button>
      <button className="sp-btn sm" disabled={!!dl} onClick={() => onDownload(doc, "duplicate")} title="Duplicado A4 horizontal">
        {dl==="duplicate" ? <span className="sp-spinner"/> : <><PiFilePdf style={{fontSize:13,color:"#6052ff"}}/> Dup.</>}
      </button>
    </div>
  );
}

/* =====================================================================
   buildPdfHtml (unchanged)
===================================================================== */
function buildPdfHtml({ proposalCode, proposalName, clientName, institutionName, institutionNif,
  proposalDesc, category, plan, billing, students, selectedServices, smsQuantity,
  customItems, discountPercent, calculation }) {

  const coverUrl  = "https://ik.imagekit.io/zks5iegia/1.png";
  const footerUrl = "https://ik.imagekit.io/zks5iegia/9.png";
  const today     = new Date().toLocaleDateString("pt-PT");
  const mult      = BILLING_MULT[billing];

  const modulesIncluded = plan?.includedModules === "todos"
    ? EDUALL_PRICING.modules : (plan?.includedModules || []);
  const modulesInline = modulesIncluded.map((m,i) => `${i+1} - ${m}`).join("; ");

  const catalogRows = (selectedServices || []).map(id => {
    const s = EDUALL_PRICING.additionalServices.find(x => x.id===id); if(!s) return "";
    let v=0;
    switch(s.billing){ case "mensal": v=s.price*(mult||12); break; case "por_sms": v=s.price*(smsQuantity||0); break; default: v=s.price; }
    return `<div class="srow"><span>${s.name}</span><span class="price">${fmtKz(v)} Kz</span></div>`;
  }).join("");

  const customRows = (customItems||[]).filter(it=>it.name.trim()).map(it => {
    const tot=lineTotal(it);
    const imgTag=it.imageUrl
      ? `<img src="${it.imageUrl}" style="width:46px;height:46px;object-fit:cover;border-radius:8px;border:1px solid #ddd;flex-shrink:0;" crossorigin="anonymous"/>`
      : `<span style="display:inline-block;width:46px;height:46px;background:#f1f5f9;border-radius:8px;border:1px solid #ddd;flex-shrink:0;"></span>`;
    return `<div class="srow srow-img">
      <span style="display:flex;align-items:center;gap:10px;">${imgTag}
        <span><strong>${it.name}</strong><br/><small style="color:#64748b;font-size:10px;">${fmtKz(parseFloat(it.price)||0)} Kz × ${it.qty} un.</small></span>
      </span><span class="price">${fmtKz(tot)} Kz</span></div>`;
  }).join("");

  const hasCatalog = (selectedServices||[]).length > 0;
  const hasCustom  = (customItems||[]).filter(it=>it.name.trim()).length > 0;
  const hasPlan    = !!plan;
  const hasModules = modulesIncluded.length > 0 && hasPlan;
  const svcTitle   = hasCatalog ? "Serviços e Produtos Adicionais" : "Produtos Adicionais";
  const descBlock  = (proposalDesc||"").trim()
    ? `<div class="section"><div class="section-title">Notas e Observações</div>
       <div class="desc-text">${proposalDesc.trim().replace(/\n/g,"<br/>")}</div></div>` : "";

  return `<style>
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Montserrat',sans-serif;font-size:11.5px;color:#000;}
    #pdf-root{width:794px;}
    .cover{width:794px;height:1123px;background:url('${coverUrl}') center/cover no-repeat;display:block;}
    @page{margin:25mm 22mm 25mm 22mm;}
    .content{width:794px;padding:54px 74px;}
    .title{font-size:22px;font-weight:700;color:#5b4dff;margin-bottom:10px;}
    .proposal-date{font-size:11.5px;font-weight:600;color:#444;margin-bottom:24px;}
    .section{margin-top:24px;page-break-inside:avoid;break-inside:avoid;}
    .section-title{font-size:13px;font-weight:700;color:#5b4dff;margin-bottom:11px;border-bottom:2px solid #eef0ff;padding-bottom:5px;}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:15px 40px;}
    .lbl{font-size:9.5px;font-weight:700;color:#888;display:block;text-transform:uppercase;letter-spacing:.5px;margin-bottom:2px;}
    .val{font-size:11.5px;font-weight:600;color:#1a1d2e;}
    .plan-box{padding:14px 16px;background:#f5f6ff;border-left:4px solid #5b4dff;border-radius:6px;font-size:12px;font-weight:700;color:#1a1d2e;}
    .plan-box .plan-name{font-size:13px;font-weight:700;color:#5b4dff;}
    .plan-detail{display:flex;justify-content:space-between;align-items:center;margin-top:8px;padding-top:8px;border-top:1px solid #dde0ff;font-size:11px;}
    .plan-detail .pd-label{font-weight:600;color:#555;}
    .plan-detail .pd-value{font-weight:800;color:#1a1d2e;font-size:12px;}
    .plan-detail .pd-value.big{font-size:13px;color:#5b4dff;}
    .modules-inline{font-size:11px;font-weight:600;line-height:1.9;color:#334155;}
    .srow{display:flex;justify-content:space-between;align-items:center;padding:9px 0;border-bottom:1px solid #eee;font-size:11px;font-weight:600;}
    .srow:last-child{border-bottom:none;}
    .srow-img{min-height:54px;}
    .price{font-weight:800;color:#1a1d2e;}
    .sub-lbl{font-size:9.5px;font-weight:700;color:#5b4dff;margin:12px 0 5px;text-transform:uppercase;letter-spacing:.5px;}
    .sum-table{width:100%;border-collapse:collapse;table-layout:fixed;}
    .sum-table td{padding:8px 0;border-bottom:1px solid #eee;font-size:11.5px;font-weight:600;color:#334155;}
    .sum-table td:last-child{text-align:right;width:180px;font-variant-numeric:tabular-nums;white-space:nowrap;}
    .sum-table tr:last-child td{border-bottom:none;}
    .sum-table .red td{color:#ef4444;}
    .sum-table .total-row td{background:#5b4dff;color:#fff;padding:11px 14px;font-size:13.5px;font-weight:800;border:none;}
    .sum-table .total-row td:first-child{border-radius:6px 0 0 6px;padding-left:14px;}
    .sum-table .total-row td:last-child{border-radius:0 6px 6px 0;padding-right:14px;}
    .desc-text{font-size:11px;font-weight:500;color:#334155;line-height:1.8;background:#f8fafc;border-left:3px solid #c7d2fe;border-radius:6px;padding:12px 14px;}
    .footer{width:794px;height:1123px;background:url('${footerUrl}') center/cover no-repeat;page-break-before:always;break-inside:avoid;display:block;}
  </style>
  <div id="pdf-root">
    <div class="cover"></div>
    <div class="content">
      <div class="title">Proposta Comercial</div>
      <div class="proposal-date">Data: <strong>${today}</strong></div>
      <div class="section">
        <div class="section-title">Identificação</div>
        <div class="grid">
          <div><span class="lbl">Código</span><span class="val">${proposalCode}</span></div>
          <div><span class="lbl">Proposta</span><span class="val">${proposalName}</span></div>
          <div><span class="lbl">Cliente</span><span class="val">${clientName}</span></div>
          <div><span class="lbl">Instituição</span><span class="val">${institutionName}</span></div>
          <div><span class="lbl">NIF</span><span class="val">${institutionNif||"—"}</span></div>
          <div><span class="lbl">Categoria</span><span class="val">${category?.name||""}</span></div>
        </div>
      </div>
      ${hasPlan ? `<div class="section">
        <div class="section-title">Plano Selecionado</div>
        <div class="plan-box">
          <div class="plan-name">${plan.name} <span style="font-weight:500;color:#666;font-size:11px;">— ${BILLING_LABELS[billing]||billing}</span></div>
          ${plan.billing==="anual_por_aluno" ? `
            <div class="plan-detail"><span class="pd-label">Valor por estudante</span><span class="pd-value">${fmtKz(plan.price)} Kz</span></div>
            <div class="plan-detail"><span class="pd-label">Nº de estudantes</span><span class="pd-value">${students}</span></div>
            <div class="plan-detail"><span class="pd-label">Total do Plano</span><span class="pd-value big">${fmtKz(plan.price)} Kz × ${students} = ${fmtKz(calculation.base)} Kz</span></div>
          ` : `<div class="plan-detail"><span class="pd-label">Valor ${BILLING_LABELS[billing]||billing}</span><span class="pd-value big">${fmtKz(calculation.base)} Kz</span></div>`}
        </div>
      </div>` : ""}
      ${hasModules ? `<div class="section"><div class="section-title">Módulos Inclusos</div><div class="modules-inline">${modulesInline}</div></div>` : ""}
      ${hasCatalog||hasCustom ? `<div class="section">
        <div class="section-title">${svcTitle}</div>
        ${catalogRows}
        ${hasCustom ? `${hasCatalog?`<div class="sub-lbl">Produtos Personalizados</div>`:""}${customRows}` : ""}
      </div>` : ""}
      <div class="section">
        <div class="section-title">Resumo Financeiro</div>
        <table class="sum-table"><tbody>
          ${hasPlan ? (plan.billing==="anual_por_aluno"
            ? `<tr><td>Plano (${fmtKz(plan.price)} Kz × ${students} estudantes)</td><td>${fmtKz(calculation.base)} Kz</td></tr>`
            : `<tr><td>Licenciamento (${BILLING_LABELS[billing]||billing})</td><td>${fmtKz(calculation.base)} Kz</td></tr>`) : ""}
          ${hasCatalog||hasCustom ? `<tr><td>${svcTitle}</td><td>${fmtKz(calculation.servicesTotal)} Kz</td></tr>` : ""}
          <tr><td>Subtotal</td><td>${fmtKz(calculation.subtotal)} Kz</td></tr>
          <tr class="red"><td>Desconto (${discountPercent}%)</td><td>− ${fmtKz(calculation.discountValue)} Kz</td></tr>
          <tr class="total-row"><td>Total Geral</td><td>${fmtKz(calculation.total)} Kz</td></tr>
        </tbody></table>
      </div>
      ${descBlock}
    </div>
    <div class="footer"></div>
  </div>`;
}

/* =====================================================================
   MAIN COMPONENT
===================================================================== */
export default function SchoolProposalModal({ toggle_btn, onSaved }) {
  const [show,    setShow]   = useState(false);
  const [tab,     setTab]    = useState("form");
  const [loading, setLoading]= useState(false);
  const [saving,  setSaving] = useState(false);

  const [savedProposal, setSavedProposal] = useState(null);

  // ── form fields ──
  const [proposalCode,    setProposalCode]    = useState("");
  const [proposalName,    setProposalName]    = useState("");
  const [clientName,      setClientName]      = useState("");
  const [institutionName, setInstitutionName] = useState("");
  const [institutionNif,  setInstitutionNif]  = useState("");
  const [proposalDesc,    setProposalDesc]    = useState("");
  const [categoryKey,     setCategoryKey]     = useState("publicSchools");
  const [planId,          setPlanId]          = useState("");
  const [billing,         setBilling]         = useState("anual");
  const [students,        setStudents]        = useState(0);
  const [smsQty,          setSmsQty]          = useState(1000);
  const [selectedServices,setSelectedServices]= useState([]);
  const [customItems,     setCustomItems]     = useState(() => DEFAULT_CUSTOM_ITEMS.map(i=>({...i})));
  const [discountPercent, setDiscountPercent] = useState(0);

  // ── docs tab state ──
  const [docs,        setDocs]        = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [dlLoading,   setDlLoading]   = useState({});

  // doc creation form
  const [selectedDocType, setSelectedDocType] = useState(null); // e.g. "FT"
  const [creatingDoc,     setCreatingDoc]     = useState(false);
  const [createError,     setCreateError]     = useState(null); // { userMsg, errorCode, technical }
  const [showErrDetail,   setShowErrDetail]   = useState(false);

  // fields for doc creation
  const [docPayment,   setDocPayment]   = useState(0);
  const [docObs,       setDocObs]       = useState("");
  const [docRefId,     setDocRefId]     = useState("");
  const [docClientName,setDocClientName]= useState("");
  const [docClientNif, setDocClientNif] = useState("");
  const [docClientEmail,setDocClientEmail]= useState("");
  const [docItems,     setDocItems]     = useState([]);

  // ── vendus tab ──
  const [vndConfig,      setVndConfig]      = useState(null);
  const [vndLoading,     setVndLoading]     = useState(false);
  const [vndSaving,      setVndSaving]      = useState(false);
  const [vndTesting,     setVndTesting]     = useState(false);
  const [vndApiKey,      setVndApiKey]      = useState("");
  const [vndRegisterId,  setVndRegisterId]  = useState("");
  const [vndCountry,     setVndCountry]     = useState("AO");
  const [vndShowKey,     setVndShowKey]     = useState(false);
  const [vndConfigDirty, setVndConfigDirty] = useState(false);
  const [payMethods,     setPayMethods]     = useState([]);
  const [pmAdding,       setPmAdding]       = useState(false);
  const [showPmForm,     setShowPmForm]     = useState(false);
  const [pmNewId,        setPmNewId]        = useState("");
  const [pmNewName,      setPmNewName]      = useState("");
  const [pmNewDefault,   setPmNewDefault]   = useState(false);
  const [pmDeletingId,   setPmDeletingId]   = useState(null);
  const [vndConfigured,  setVndConfigured]  = useState(false);

  useEffect(() => { if(show) setProposalCode(genCode()); }, [show]);

  const category = EDUALL_PRICING.categories[categoryKey];
  const plan     = category?.plans.find(p => p.id===planId);
  const mult     = BILLING_MULT[billing];

  const handleItemChange = useCallback((id,f,v) => setCustomItems(prev=>prev.map(it=>it.id===id?{...it,[f]:v}:it)), []);
  const handleItemRemove = useCallback((id) => setCustomItems(prev=>prev.filter(it=>it.id!==id)), []);
  const handleAddItem    = () => setCustomItems(prev=>[...prev,{id:uid(),name:"",price:0,qty:1,imageUrl:""}]);
  const customTotal      = useMemo(() => customItems.reduce((a,it)=>a+lineTotal(it),0), [customItems]);

  const calculation = useMemo(() => {
    let base = 0;
    if(plan) base = plan.billing==="anual_por_aluno" ? plan.price*students : plan.price*(mult||12);
    let svcTotal = 0;
    selectedServices.forEach(id => {
      const s=EDUALL_PRICING.additionalServices.find(x=>x.id===id); if(!s) return;
      switch(s.billing){
        case "mensal":  svcTotal+=s.price*(mult||12); break;
        case "por_sms": svcTotal+=s.price*smsQty;     break;
        default:        svcTotal+=s.price;
      }
    });
    svcTotal += customTotal;
    const subtotal      = base+svcTotal;
    const discountValue = (subtotal*discountPercent)/100;
    const total         = subtotal-discountValue;
    return { base, servicesTotal:svcTotal, subtotal, discountValue, total };
  }, [plan, students, mult, selectedServices, smsQty, customTotal, discountPercent]);

  // ── When selectedDocType changes, pre-populate items from proposal ──
  useEffect(() => {
    if (!selectedDocType) return;
    const info = VENDUS_DOC_TYPES[selectedDocType];
    if (!info?.needsItems) { setDocItems([]); return; }
    const items = [];
    if (plan) items.push({ id:uid(), title:`Licenciamento — ${plan.name} (${BILLING_LABELS[billing]||billing})`, price: calculation.base, quantity:1, discount: Number(discountPercent)||0, tax:false });
    selectedServices.forEach(id => {
      const s=EDUALL_PRICING.additionalServices.find(x=>x.id===id); if(!s) return;
      let v=0;
      switch(s.billing){ case "mensal": v=s.price*(mult||12); break; case "por_sms": v=s.price*smsQty; break; default: v=s.price; }
      items.push({ id:uid(), title:s.name, price:v, quantity:1, discount:Number(discountPercent)||0, tax:false });
    });
    customItems.filter(it=>it.name.trim()).forEach(it=>{
      items.push({ id:uid(), title:it.name, price:Number(it.price)||0, quantity:Number(it.qty)||1, discount:Number(discountPercent)||0, tax:false });
    });
    if (items.length === 0) items.push({ id:uid(), title:"", price:0, quantity:1, discount:0, tax:false });
    setDocItems(items);
    // pre-fill client
    setDocClientName(clientName || "");
    setDocClientNif(institutionNif || "");
    setDocObs(proposalDesc || "");
    setCreateError(null);
    setShowErrDetail(false);
  }, [selectedDocType]);

  // ── Vendus config ──
  const loadVendusConfig = useCallback(async () => {
    try {
      setVndLoading(true);
      const [cfgRes, pmRes] = await Promise.all([
        axios.get(Hoot()+"vendus/config",          {withCredentials:true}),
        axios.get(Hoot()+"vendus/payment-methods", {withCredentials:true}),
      ]);
      const cfg = cfgRes.data;
      setVndConfig(cfg);
      if(cfg){
        setVndApiKey(cfg.api_key || "");
        setVndRegisterId(String(cfg.register_id || ""));
        setVndCountry(cfg.country || "AO");
        setVndConfigured(!!cfg.has_key);
      }
      setPayMethods(Array.isArray(pmRes.data) ? pmRes.data : []);
    } catch(e) { /* silent */ }
    finally { setVndLoading(false); setVndConfigDirty(false); }
  }, []);

  useEffect(() => { if(tab==="vendus"&&show) loadVendusConfig(); }, [tab,show,loadVendusConfig]);

  const handleSaveVndConfig = async () => {
    if(!vndRegisterId) return toast.error("Register ID é obrigatório.");
    try {
      setVndSaving(true);
      await axios.post(Hoot()+"vendus/config", {
        api_key: vndApiKey.trim(), register_id: Number(vndRegisterId), country: vndCountry,
      }, {withCredentials:true});
      toast.success("Configuração Vendus guardada!");
      setVndConfigDirty(false);
      await loadVendusConfig();
    } catch(e) {
      toast.error(e.response?.data?.userMsg || e.response?.data?.msg || "Erro ao guardar configuração.");
    } finally { setVndSaving(false); }
  };

  const handleTestVendus = async () => {
    try {
      setVndTesting(true);
      const r = await axios.post(Hoot()+"vendus/invoice", { _raw:true }, {withCredentials:true});
      toast.success(`✅ Vendus OK! Documento de teste: ${r.data?.number || r.data?.id}`);
    } catch(e) {
      const d = e.response?.data;
      toast.error(d?.userMsg || d?.msg || "Erro ao testar Vendus.");
    } finally { setVndTesting(false); }
  };

  const handleReEncrypt = async () => {
    if(!vndApiKey.trim()) return toast.error("Introduz a chave de API primeiro.");
    try {
      setVndSaving(true);
      await axios.post(Hoot()+"vendus/reencrypt", { api_key: vndApiKey.trim() }, {withCredentials:true});
      toast.success("Chave re-encriptada com sucesso!");
      await loadVendusConfig();
    } catch(e) {
      toast.error(e.response?.data?.userMsg || e.response?.data?.msg || "Erro na re-encriptação.");
    } finally { setVndSaving(false); }
  };

  const handleAddPaymentMethod = async () => {
    if(!pmNewId || !pmNewName.trim()) return toast.error("ID Vendus e nome são obrigatórios.");
    try {
      setPmAdding(true);
      await axios.post(Hoot()+"vendus/payment-methods", {
        vendus_id: Number(pmNewId), name: pmNewName.trim(), is_default: pmNewDefault,
      }, {withCredentials:true});
      toast.success("Método adicionado!");
      setPmNewId(""); setPmNewName(""); setPmNewDefault(false); setShowPmForm(false);
      const r = await axios.get(Hoot()+"vendus/payment-methods", {withCredentials:true});
      setPayMethods(Array.isArray(r.data) ? r.data : []);
    } catch(e) {
      toast.error(e.response?.data?.userMsg || e.response?.data?.msg || "Erro ao adicionar método.");
    } finally { setPmAdding(false); }
  };

  const handleDeletePaymentMethod = async (pmId) => {
    try {
      setPmDeletingId(pmId);
      await axios.delete(Hoot()+`vendus/payment-methods/${pmId}`, {withCredentials:true});
      setPayMethods(prev=>prev.filter(m=>m.id!==pmId));
      toast.success("Método removido.");
    } catch { toast.error("Erro ao remover método."); }
    finally { setPmDeletingId(null); }
  };

  // ── Save proposal ──
  const handleSave = async () => {
    if(!proposalName||!clientName||!institutionName)
      return toast.error("Preencha o nome da proposta, cliente e instituição.");
    try {
      setSaving(true);
      const payload = {
        code: proposalCode, name: proposalName,
        clientName, institutionName, institutionNif,
        description: proposalDesc, categoryKey,
        planId: planId||null, billing,
        students: Number(students)||0, selectedServices,
        smsQty: Number(smsQty)||0, customItems,
        discountPercent: Number(discountPercent)||0,
        totalValue: calculation.total,
        calculationSnapshot: calculation,
      };
      const r = await axios.post(Hoot()+"api/proposals", payload, {withCredentials:true});
      const saved = r.data?.data||r.data;
      setSavedProposal(saved);
      toast.success("Proposta guardada!");
      if(onSaved) onSaved(saved);
      setTab("docs");
      fetchDocs(saved.id);
    } catch(e) {
      toast.error(e.response?.data?.userMsg || e.response?.data?.msg || "Erro ao guardar proposta.");
    } finally { setSaving(false); }
  };

  // ── Export PDF ──
  const handleExportPdf = async () => {
    if(!proposalName||!clientName||!institutionName)
      return toast.error("Preencha os dados principais.");
    try {
      setLoading(true);
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px;top:0;";
      container.innerHTML = buildPdfHtml({
        proposalCode, proposalName, clientName, institutionName,
        institutionNif, proposalDesc, category, plan, billing, students,
        selectedServices, smsQuantity:smsQty, customItems, discountPercent, calculation,
      });
      document.body.appendChild(container);
      await html2pdf().from(document.getElementById("pdf-root")).set({
        margin:0, filename:`${proposalCode}.pdf`,
        html2canvas:{scale:2,useCORS:true,allowTaint:true},
        jsPDF:{unit:"mm",format:"a4"},
        pagebreak:{mode:["css"]},
      }).save();
      document.body.removeChild(container);
      toast.success("PDF gerado!");
    } catch { toast.error("Erro ao gerar PDF."); }
    finally { setLoading(false); }
  };

  // ── Fetch docs ──
  const fetchDocs = useCallback(async (pid) => {
    const id = pid || savedProposal?.id; if(!id) return;
    try {
      setLoadingDocs(true);
      const r = await axios.get(Hoot()+`api/proposals/${id}/documents`, {withCredentials:true});
      setDocs(r.data?.data||r.data||[]);
    } catch { toast.error("Erro ao carregar documentos."); }
    finally { setLoadingDocs(false); }
  }, [savedProposal?.id]);

  useEffect(() => { if(tab==="docs"&&savedProposal) fetchDocs(); }, [tab,savedProposal]);

  // ── Create document (all Vendus types + proposta local) ──
  const handleCreateDoc = async () => {
    if(!selectedDocType) return toast.warning("Seleccione o tipo de documento.");
    const pid = savedProposal?.id;
    if(!pid) return toast.error("Guarde a proposta primeiro.");

    setCreateError(null);
    setShowErrDetail(false);

    const typeInfo = VENDUS_DOC_TYPES[selectedDocType];

    // Validate items if needed
    if (typeInfo?.needsItems && selectedDocType !== "proposta") {
      const validItems = docItems.filter(it => it.title.trim());
      if (!validItems.length) return toast.error("Adiciona pelo menos um artigo com descrição.");
    }

    let payload;

    if (selectedDocType === "proposta") {
      // local PDF record only
      payload = { type:"proposta", obs: docObs || null };
    } else {
      // vendus document
      const vendusItems = docItems
        .filter(it => it.title.trim())
        .map(it => ({
          title:    it.title,
          quantity: Number(it.quantity)||1,
          price:    Number(it.price)||0,
          discount: Number(it.discount)||0,
          tax:      !!it.tax,
        }));

      payload = {
        type:    selectedDocType,
        obs:     docObs || "",
        payment: typeInfo?.needsPayment ? Number(docPayment) : undefined,
        refDocId: docRefId.trim() || undefined,
        cliente: {
          name:      docClientName || clientName,
          fiscal_id: docClientNif  || institutionNif || "000000000",
          address:   "",
          email:     docClientEmail || "sales@eduall.ao",
          country:   vndConfig?.country || "AO",
        },
        itens: vendusItems,
      };
    }

    try {
      setCreatingDoc(true);
      const r = await axios.post(
        Hoot()+`api/proposals/${pid}/documents`,
        payload,
        {withCredentials:true}
      );
      const newDoc = r.data?.data || r.data;
      setDocs(prev => [newDoc, ...prev]);
      toast.success(`${typeInfo?.label || selectedDocType} criado com sucesso!`);
      setSelectedDocType(null);
      setDocItems([]);
    } catch(e) {
      const d = e.response?.data;
      // Surface the server's structured error to the user
      setCreateError({
        userMsg:   d?.userMsg   || d?.msg    || "Erro inesperado ao criar documento.",
        errorCode: d?.errorCode || "UNKNOWN",
        technical: d?.technical || String(e),
      });
      toast.error(d?.userMsg || d?.msg || "Erro ao criar documento.");
    } finally { setCreatingDoc(false); }
  };

  // ── Download doc ──
  const handleDownload = async (doc, kind) => {
    const key = doc.id;
    setDlLoading(p=>({...p,[key]:kind}));
    try {
      if(doc.type==="proposta"){
        const container = document.createElement("div");
        container.style.cssText = "position:fixed;left:-9999px;top:0;";
        container.innerHTML = buildPdfHtml({
          proposalCode, proposalName, clientName, institutionName,
          institutionNif, proposalDesc, category, plan, billing, students,
          selectedServices, smsQuantity:smsQty, customItems, discountPercent, calculation,
        });
        document.body.appendChild(container);
        await html2pdf().from(document.getElementById("pdf-root")).set({
          margin:0, filename:`${proposalCode}.pdf`,
          html2canvas:{scale:2,useCORS:true,allowTaint:true},
          jsPDF:{unit:"mm",format:"a4"},
          pagebreak:{mode:["css"]},
        }).save();
        document.body.removeChild(container);
        return;
      }
      const vId = doc.vendusId || doc.vendus_id || doc.id;
      let origUrl = cacheGet(vId,"original");
      if(!origUrl){
        const r = await axios.get(Hoot()+`eduallinvoicedoc/${vId}/pdf`,{responseType:"blob",withCredentials:true});
        origUrl = URL.createObjectURL(new Blob([r.data],{type:"application/pdf"}));
        cacheSet(vId,"original",origUrl);
      }
      if(kind==="original"){
        triggerDl(origUrl,`${selectedDocType||doc.type}_${doc.number||vId}.pdf`);
      } else {
        const dup = await buildDuplicatePdf(vId, origUrl);
        triggerDl(dup,`${selectedDocType||doc.type}_duplicado_${doc.number||vId}.pdf`);
      }
      toast.success("A descarregar…");
    } catch(e) {
      const d = e.response?.data;
      toast.error(d?.userMsg || d?.msg || "Erro ao baixar documento.");
    } finally { setDlLoading(p=>({...p,[key]:null})); }
  };

  /* ─────────────────────────────────────
     RENDER: FORM TAB
  ───────────────────────────────────── */
  const renderForm = () => (
    <>
      <div className="sp-section">
        <div className="sp-section-title"><FiFileText style={{fontSize:13}}/> Dados da Proposta</div>
        <div className="sp-card">
          <div style={{marginBottom:8}}>
            <div className="sp-label">Código (automático)</div>
            <input className="sp-input" value={proposalCode} disabled style={{opacity:.55}}/>
          </div>
          <div className="sp-grid-2" style={{marginBottom:8}}>
            <div><div className="sp-label">Nome da Proposta *</div><input className="sp-input" placeholder="Ex: Proposta IMEL 2025" value={proposalName} onChange={e=>setProposalName(e.target.value)}/></div>
            <div><div className="sp-label">Nome do Cliente *</div><input className="sp-input" placeholder="Nome do responsável" value={clientName} onChange={e=>setClientName(e.target.value)}/></div>
          </div>
          <div className="sp-grid-2" style={{marginBottom:8}}>
            <div><div className="sp-label">Instituição *</div><input className="sp-input" placeholder="Nome da escola/universidade" value={institutionName} onChange={e=>setInstitutionName(e.target.value)}/></div>
            <div><div className="sp-label">NIF</div><input className="sp-input" placeholder="NIF da instituição" value={institutionNif} onChange={e=>setInstitutionNif(e.target.value)}/></div>
          </div>
          <div><div className="sp-label">Notas / Observações</div>
            <textarea className="sp-input" rows={3} placeholder="Esta proposta é válida por 30 dias…" value={proposalDesc} onChange={e=>setProposalDesc(e.target.value)} style={{resize:"vertical"}}/>
          </div>
        </div>
      </div>

      <div className="sp-section">
        <div className="sp-section-title"><FiList style={{fontSize:13}}/> Plano e Faturação</div>
        <div className="sp-card">
          <div className="sp-grid-2" style={{marginBottom:10}}>
            <div>
              <div className="sp-label">Categoria</div>
              <select className="sp-select" value={categoryKey} onChange={e=>{setCategoryKey(e.target.value);setPlanId("");}}>
                {Object.keys(EDUALL_PRICING.categories).map(k=>
                  <option key={k} value={k}>{EDUALL_PRICING.categories[k].name}</option>)}
              </select>
            </div>
            <div>
              <div className="sp-label">Período de faturação</div>
              <select className="sp-select" value={billing} onChange={e=>setBilling(e.target.value)}>
                {Object.entries(BILLING_LABELS).map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="sp-label" style={{marginBottom:8}}>Plano (opcional)</div>
          {category?.plans.map(p => (
            <div key={p.id} className={`sp-plan-card ${planId===p.id?"sel":""}`} onClick={()=>setPlanId(planId===p.id?"":p.id)}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div className="sp-plan-check">{planId===p.id&&<FiCheck style={{fontSize:10,color:"#fff"}}/>}</div>
                <div style={{flex:1}}>
                  <div className="sp-plan-name">{p.name}</div>
                  <div className="sp-plan-price">
                    {p.billing==="anual_por_aluno"
                      ? `${fmtKz(p.price)} Kz/aluno (mín. ${p.minimumStudents||350} alunos)`
                      : `${fmtKz(p.price)} Kz/mês · ${fmtKz(p.price*(mult||12))} Kz (${BILLING_LABELS[billing]})`}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {plan?.billing==="anual_por_aluno"&&(
            <div style={{marginTop:10}}>
              <div className="sp-label">Número de estudantes</div>
              <input className="sp-input" type="number" value={students} onChange={e=>setStudents(Number(e.target.value))}/>
            </div>
          )}
          {plan&&(
            <div style={{marginTop:12}}>
              <div className="sp-label" style={{marginBottom:6}}>Módulos incluídos</div>
              <div className="sp-modules">
                {(plan.includedModules==="todos"?EDUALL_PRICING.modules:plan.includedModules).map(m=>
                  <span key={m} className="sp-module-tag">{m}</span>)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="sp-section">
        <div className="sp-section-title"><FiPackage style={{fontSize:13}}/> Serviços Adicionais</div>
        <div className="sp-card">
          {EDUALL_PRICING.additionalServices.map(svc => {
            const on = selectedServices.includes(svc.id);
            let priceLabel = `${fmtKz(svc.price)} Kz · ${svc.unit}`;
            if(on&&svc.billing==="mensal")  priceLabel=`${fmtKz(svc.price*(mult||12))} Kz (${BILLING_LABELS[billing]})`;
            if(on&&svc.billing==="por_sms") priceLabel=`${fmtKz(svc.price*smsQty)} Kz (${fmtKz(smsQty)} SMS)`;
            return (
              <div key={svc.id}>
                <div className="sp-svc-row">
                  <div style={{flex:1}}>
                    <div className="sp-svc-name">{svc.name}</div>
                    <div className="sp-svc-price">{priceLabel}</div>
                  </div>
                  <Toggle checked={on} onChange={v=>setSelectedServices(p=>v?[...p,svc.id]:p.filter(x=>x!==svc.id))}/>
                </div>
                {on&&svc.billing==="por_sms"&&(
                  <div style={{padding:"6px 0 8px"}}>
                    <div className="sp-label">Quantidade de SMS</div>
                    <input className="sp-input" type="number" min="1" value={smsQty} onChange={e=>setSmsQty(Number(e.target.value))}/>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="sp-section">
        <div className="sp-section-title"><FiPackage style={{fontSize:13}}/> Produtos / Serviços Personalizados</div>
        {customItems.map(it=><CustomProductCard key={it.id} item={it} onChange={handleItemChange} onRemove={handleItemRemove}/>)}
        <button className="add-item-btn" onClick={handleAddItem}><FiPlus style={{fontSize:13}}/> Adicionar Produto / Serviço</button>
      </div>

      <div className="sp-section">
        <div className="sp-section-title"><FiPercent style={{fontSize:13}}/> Desconto Comercial</div>
        <div className="sp-card">
          <div className="sp-grid-2">
            <div>
              <div className="sp-label">Desconto (%)</div>
              <input className="sp-input" type="number" min="0" max="100" value={discountPercent} onChange={e=>setDiscountPercent(Number(e.target.value))}/>
            </div>
            <div>
              <div className="sp-label">Valor do desconto</div>
              <div style={{padding:"8px 11px",borderRadius:10,background:"rgba(239,68,68,.07)",border:"1px solid rgba(239,68,68,.2)",fontSize:".87rem",fontWeight:950,color:"#ef4444"}}>
                − {fmtKz(calculation.discountValue)} Kz
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sp-section">
        <div className="sp-section-title"><FiDollarSign style={{fontSize:13}}/> Resumo Financeiro</div>
        <div className="sp-summary">
          {plan&&<div className="sp-sum-row"><span>Licenciamento ({BILLING_LABELS[billing]})</span><span className="sp-sum-val">{fmtKz(calculation.base)} Kz</span></div>}
          {(selectedServices.length>0||customItems.some(it=>it.name.trim()))&&
            <div className="sp-sum-row"><span>Serviços / Produtos</span><span className="sp-sum-val">{fmtKz(calculation.servicesTotal)} Kz</span></div>}
          <div className="sp-sum-row"><span>Subtotal</span><span className="sp-sum-val">{fmtKz(calculation.subtotal)} Kz</span></div>
          <div className="sp-sum-row discount"><span>Desconto ({discountPercent}%)</span><span className="sp-sum-val">− {fmtKz(calculation.discountValue)} Kz</span></div>
          <div className="sp-sum-row total"><span>Total Geral</span><span className="sp-sum-val">{fmtKz(calculation.total)} Kz</span></div>
        </div>
      </div>
    </>
  );

  /* ─────────────────────────────────────
     RENDER: DOCS TAB (fully reworked)
  ───────────────────────────────────── */
  const renderDocs = () => {
    const typeInfo = selectedDocType ? VENDUS_DOC_TYPES[selectedDocType] : null;

    return (
      <>
        {/* ── Must save first ── */}
        {!savedProposal && (
          <div className="sp-alert warn" style={{marginBottom:16}}>
            <FiAlertCircle style={{flexShrink:0,marginTop:1}}/>
            Guarda a proposta primeiro (botão "Guardar") para poderes emitir documentos.
          </div>
        )}

        {/* ── Doc type selector ── */}
        {savedProposal && (
          <div className="sp-card" style={{marginBottom:0}}>
            <div className="sp-section-title" style={{marginBottom:10}}>
              <FiSend style={{fontSize:12}}/> Emitir novo documento
            </div>

            {/* Type grid — includes "proposta" local + all Vendus types */}
            <div className="dt-grid">
              {/* PDF local */}
              <div
                className={`dt-tile ${selectedDocType==="proposta"?"sel":""}`}
                style={{"--sel-color":"#6052ff","--sel-bg":"rgba(96,82,255,.06)"}}
                onClick={() => setSelectedDocType(selectedDocType==="proposta"?null:"proposta")}
                title="PDF gerado localmente pelo browser"
              >
                <div className="dt-tile-icon">📋</div>
                <div className="dt-tile-code" style={{color:selectedDocType==="proposta"?"#6052ff":"var(--text,#0f172a)"}}>PDF</div>
                <div className="dt-tile-label">Proposta Local</div>
              </div>

              {Object.entries(VENDUS_DOC_TYPES).map(([code, info]) => (
                <DocTypeTile
                  key={code}
                  code={code}
                  info={info}
                  selected={selectedDocType===code}
                  onSelect={c => setSelectedDocType(selectedDocType===c ? null : c)}
                />
              ))}
            </div>

            {/* ── Creation form (dynamic per type) ── */}
            {selectedDocType && (
              <div className="dc-form">
                <div className="dc-form-header">
                  <span style={{fontSize:"1.1rem"}}>{selectedDocType==="proposta"?"📋":typeInfo?.icon}</span>
                  <span style={{flex:1}}>
                    {selectedDocType==="proposta" ? "Proposta PDF Local" : `${typeInfo?.label} (${selectedDocType})`}
                  </span>
                  <span style={{fontSize:".71rem",color:"var(--muted,#6b7280)",fontWeight:600}}>
                    {selectedDocType==="proposta" ? "Gerado localmente, sem chamada à API" : typeInfo?.desc}
                  </span>
                  <button className="sp-icon-btn" style={{marginLeft:8}} onClick={()=>{setSelectedDocType(null);setCreateError(null);}}>
                    <FiX style={{fontSize:13}}/>
                  </button>
                </div>

                <div className="dc-form-body">

                  {/* error from last attempt */}
                  {createError && (
                    <div className="sp-alert err" style={{marginBottom:0,flexDirection:"column",gap:6}}>
                      <div style={{display:"flex",alignItems:"flex-start",gap:8}}>
                        <FiAlertCircle style={{flexShrink:0,marginTop:1}}/>
                        <div style={{flex:1}}>
                          <strong>[{createError.errorCode}]</strong> {createError.userMsg}
                        </div>
                        <button className="sp-btn sm" style={{padding:"2px 8px",fontSize:".68rem"}}
                          onClick={()=>setShowErrDetail(v=>!v)}>
                          {showErrDetail?"Ocultar detalhe":"Ver detalhe"}
                        </button>
                      </div>
                      {showErrDetail && (
                        <div className="err-detail">
                          <pre>{createError.technical}</pre>
                        </div>
                      )}
                    </div>
                  )}

                  {/* If local PDF — just obs */}
                  {selectedDocType === "proposta" && (
                    <div>
                      <div className="sp-label">Notas (opcional)</div>
                      <textarea className="sp-input" rows={2} placeholder="Observações para este registo…"
                        value={docObs} onChange={e=>setDocObs(e.target.value)} style={{resize:"vertical"}}/>
                    </div>
                  )}

                  {/* Vendus types */}
                  {selectedDocType !== "proposta" && (
                    <>
                      {/* Client data */}
                      <div>
                        <div className="sp-section-title" style={{marginBottom:8}}>Dados do Cliente</div>
                        <div className="sp-grid-3">
                          <div>
                            <div className="sp-label">Nome *</div>
                            <input className="sp-input" value={docClientName} onChange={e=>setDocClientName(e.target.value)} placeholder="Nome do cliente"/>
                          </div>
                          <div>
                            <div className="sp-label">NIF</div>
                            <input className="sp-input" value={docClientNif} onChange={e=>setDocClientNif(e.target.value)} placeholder="NIF / BI"/>
                          </div>
                          <div>
                            <div className="sp-label">Email</div>
                            <input className="sp-input" type="email" value={docClientEmail} onChange={e=>setDocClientEmail(e.target.value)} placeholder="email@empresa.ao"/>
                          </div>
                        </div>
                      </div>

                      {/* Payment method — only for types that need it */}
                      {typeInfo?.needsPayment && (
                        <div className="sp-grid-2">
                          <div>
                            <div className="sp-label">Método de Pagamento</div>
                            <select className="sp-select" value={docPayment} onChange={e=>setDocPayment(Number(e.target.value))}>
                              {PAYMENT_METHOD_LABELS.map((l,i)=><option key={i} value={i}>{l}</option>)}
                            </select>
                          </div>
                          <div>
                            <div className="sp-label">Observações</div>
                            <input className="sp-input" value={docObs} onChange={e=>setDocObs(e.target.value)} placeholder="Notas no documento…"/>
                          </div>
                        </div>
                      )}

                      {/* Ref document (NC, ND, RC) */}
                      {typeInfo?.canRef && (
                        <div className="ref-doc-box">
                          <div className="sp-label" style={{marginBottom:5}}>
                            ID do Documento de Referência
                            <span style={{marginLeft:6,fontWeight:600,color:"#92400e",fontSize:".68rem"}}>
                              (ex: NC referencia FT)
                            </span>
                          </div>
                          <input className="sp-input" value={docRefId} onChange={e=>setDocRefId(e.target.value)}
                            placeholder="ID Vendus do documento original…"/>
                        </div>
                      )}

                      {/* Items table */}
                      {typeInfo?.needsItems && (
                        <ItemsEditor items={docItems} onChange={setDocItems}/>
                      )}

                      {/* Obs for types without payment */}
                      {!typeInfo?.needsPayment && (
                        <div>
                          <div className="sp-label">Observações</div>
                          <input className="sp-input" value={docObs} onChange={e=>setDocObs(e.target.value)} placeholder="Notas no documento…"/>
                        </div>
                      )}
                    </>
                  )}

                  {/* Submit button */}
                  <div style={{display:"flex",gap:8}}>
                    <button className="sp-btn primary" onClick={handleCreateDoc} disabled={creatingDoc}>
                      {creatingDoc
                        ? <><span className="sp-spinner" style={{borderTopColor:"#fff"}}/> A criar…</>
                        : <><FiSend style={{fontSize:12}}/>
                            {selectedDocType==="proposta" ? " Registar proposta" : ` Emitir ${selectedDocType}`}
                          </>}
                    </button>
                    <button className="sp-btn" onClick={()=>{setSelectedDocType(null);setCreateError(null);}}>
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Documents table ── */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",margin:"16px 0 8px"}}>
          <div style={{fontWeight:900,fontSize:".86rem"}}>
            Documentos emitidos
            {docs.length > 0 && (
              <span style={{marginLeft:8,fontSize:".72rem",fontWeight:700,color:"var(--muted,#6b7280)"}}>
                ({docs.length})
              </span>
            )}
          </div>
          {savedProposal && (
            <button className="sp-icon-btn" onClick={()=>fetchDocs()} disabled={loadingDocs} title="Recarregar">
              <FiRefreshCw style={{fontSize:13}}/>
            </button>
          )}
        </div>

        {loadingDocs ? (
          <div className="sp-empty"><span className="sp-spinner" style={{width:22,height:22,borderWidth:3}}/></div>
        ) : docs.length === 0 ? (
          <div className="sp-empty">
            <FiFileText style={{fontSize:28,opacity:.3,display:"block",margin:"0 auto 10px"}}/>
            {savedProposal
              ? "Nenhum documento emitido. Usa o painel acima para criar."
              : "Guarda a proposta para poder emitir documentos."}
          </div>
        ) : (
          <div className="docs-table-wrap">
            <table className="docs-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Nº / Ref.</th>
                  <th>Valor</th>
                  <th>Criado por</th>
                  <th>Data</th>
                  <th>Fonte</th>
                  <th>Acções</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => (
                  <tr key={doc.id}>
                    <td><DocTypeBadge type={doc.type}/></td>
                    <td style={{fontFamily:"monospace",fontSize:".76rem",fontWeight:800,color:"var(--text,#0f172a)"}}>
                      {doc.number || doc.vendusId || "—"}
                    </td>
                    <td style={{fontVariantNumeric:"tabular-nums",whiteSpace:"nowrap"}}>
                      {doc.total_value != null ? `${fmtKz(doc.total_value)} Kz` : "—"}
                    </td>
                    <td style={{maxWidth:110,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"var(--muted,#6b7280)"}}>
                      {doc.created_by || "—"}
                    </td>
                    <td style={{whiteSpace:"nowrap",color:"var(--muted,#6b7280)",fontSize:".72rem"}}>
                      {fmtDate(doc.created_at)}
                    </td>
                    <td>
                      {doc.source ? (
                        <span style={{fontSize:".65rem",fontWeight:750,padding:"2px 7px",borderRadius:999,
                          background:"rgba(148,163,184,.1)",color:"var(--muted,#6b7280)"}}>
                          {doc.source}
                        </span>
                      ) : "—"}
                    </td>
                    <td>
                      <DownloadActions doc={doc} dlLoading={dlLoading} onDownload={handleDownload}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  /* ─────────────────────────────────────
     RENDER: VENDUS TAB
  ───────────────────────────────────── */
  const renderVendus = () => {
    if(vndLoading) return (
      <div className="sp-empty">
        <span className="sp-spinner" style={{width:22,height:22,borderWidth:3}}/>
        <div style={{marginTop:10}}>A carregar configuração…</div>
      </div>
    );

    const keyCorrupted = vndConfig?.key_corrupted;
    const hasOwnKey    = vndConfig?.has_key;

    return (
      <>
        <div className="vnd-config-card">
          <div className="vnd-config-header">
            <div className="vnd-config-header-icon"><FiShield/></div>
            <div style={{flex:1}}>
              <div className="vnd-config-header-title">Chave de API Vendus</div>
              <div className="vnd-config-header-sub">Autenticação e configuração do terminal</div>
            </div>
            {hasOwnKey&&!keyCorrupted
              ? <span className="vnd-status ok"><FiCheck style={{fontSize:10}}/> Configurado</span>
              : keyCorrupted
                ? <span className="vnd-status err">⚠ Chave corrompida</span>
                : <span className="vnd-status warn">Não configurado</span>}
          </div>
          <div className="vnd-config-body">
            {keyCorrupted&&(
              <div className="sp-alert warn">
                <FiAlertCircle style={{flexShrink:0}}/>
                <span>A chave guardada não pode ser desencriptada. Introduz a chave original novamente e clica em <strong>Re-encriptar</strong>.</span>
              </div>
            )}
            {!hasOwnKey&&!keyCorrupted&&(
              <div className="sp-alert info">
                <FiZap style={{flexShrink:0}}/>
                <span>Sem chave própria. As faturas usarão a <strong>chave padrão Eduall</strong>. Para usar a tua conta Vendus, preenche os campos abaixo.</span>
              </div>
            )}
            <div>
              <div className="sp-label">Chave de API Vendus</div>
              <div className="sp-input-wrap">
                <input className="sp-input" type={vndShowKey?"text":"password"}
                  placeholder={hasOwnKey&&!keyCorrupted?"••••••••••••••••••••• (guardada)":"Cole a chave aqui"}
                  value={vndApiKey} onChange={e=>{setVndApiKey(e.target.value);setVndConfigDirty(true);}}/>
                <button className="sp-input-eye" onClick={()=>setVndShowKey(v=>!v)}>
                  {vndShowKey?<FiEyeOff size={14}/>:<FiEye size={14}/>}
                </button>
              </div>
            </div>
            <div className="sp-grid-2">
              <div>
                <div className="sp-label">Register ID (terminal)</div>
                <input className="sp-input" type="number" placeholder="Ex: 113682273"
                  value={vndRegisterId} onChange={e=>{setVndRegisterId(e.target.value);setVndConfigDirty(true);}}/>
              </div>
              <div>
                <div className="sp-label">País</div>
                <select className="sp-select" value={vndCountry}
                  onChange={e=>{setVndCountry(e.target.value);setVndConfigDirty(true);}}>
                  {COUNTRY_OPTIONS.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button className="sp-btn primary" onClick={handleSaveVndConfig} disabled={vndSaving||!vndConfigDirty}>
                {vndSaving?<><span className="sp-spinner" style={{borderTopColor:"#fff"}}/> A guardar…</>:<><FiSave style={{fontSize:13}}/> Guardar</>}
              </button>
              {keyCorrupted&&(
                <button className="sp-btn danger" onClick={handleReEncrypt} disabled={vndSaving||!vndApiKey.trim()}>
                  <FiShield style={{fontSize:13}}/> Re-encriptar
                </button>
              )}
              <button className="sp-btn" onClick={handleTestVendus} disabled={vndTesting}>
                {vndTesting?<><span className="sp-spinner"/> A testar…</>:<><FiZap style={{fontSize:13}}/> Testar API</>}
              </button>
              <button className="sp-btn" onClick={loadVendusConfig} disabled={vndLoading}>
                <FiRefreshCw style={{fontSize:12}}/> Recarregar
              </button>
            </div>
          </div>
        </div>

        {vndConfig&&(
          <div className="vnd-config-card" style={{marginBottom:16}}>
            <div className="vnd-config-header">
              <div className="vnd-config-header-icon"><FiGlobe/></div>
              <div>
                <div className="vnd-config-header-title">Endpoints Activos</div>
                <div className="vnd-config-header-sub">URLs da API em uso para este instituto</div>
              </div>
            </div>
            <div className="vnd-config-body">
              <div>
                <div className="sp-label">API Base (v1.1)</div>
                <div style={{fontFamily:"monospace",fontSize:".76rem",color:"var(--text,#0f172a)",
                  background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,padding:"7px 10px",wordBreak:"break-all"}}>
                  {vndConfig.api_base_url||"https://www.vendus.co.ao/ws/v1.1"}
                </div>
              </div>
              <div>
                <div className="sp-label">API Legada (fallback)</div>
                <div style={{fontFamily:"monospace",fontSize:".76rem",color:"var(--muted,#6b7280)",
                  background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,padding:"7px 10px",wordBreak:"break-all"}}>
                  {vndConfig.legacy_api_url||"https://www.vendus.co.ao/ws/documents/"}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="vnd-config-card">
          <div className="vnd-config-header">
            <div className="vnd-config-header-icon"><FiCreditCard/></div>
            <div style={{flex:1}}>
              <div className="vnd-config-header-title">Métodos de Pagamento</div>
              <div className="vnd-config-header-sub">{payMethods.length} configurado(s) · usados na criação de faturas</div>
            </div>
            <button className="sp-btn" style={{padding:"5px 12px",fontSize:".75rem"}} onClick={()=>setShowPmForm(v=>!v)}>
              <FiPlus style={{fontSize:12}}/> Adicionar
            </button>
          </div>
          <div className="vnd-config-body">
            {payMethods.length===0
              ? <div style={{textAlign:"center",padding:"12px 0",color:"var(--muted,#6b7280)",fontSize:".8rem"}}>
                  Nenhum método configurado.<br/>
                  <span style={{fontSize:".72rem"}}>Serão usados os métodos padrão da conta Vendus.</span>
                </div>
              : payMethods.map(m=>(
                  <div key={m.id} className={`pm-row ${m.is_default?"pm-row-default":""}`}>
                    <div className="pm-icon"><FiCreditCard style={{fontSize:14}}/></div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:".82rem",fontWeight:800}}>{m.name}</div>
                      <div style={{fontSize:".69rem",color:"var(--muted,#6b7280)"}}>ID Vendus: {m.vendus_id}</div>
                    </div>
                    {m.is_default&&<span className="pm-badge-default">Padrão</span>}
                    <button className="sp-icon-btn" title="Remover"
                      disabled={pmDeletingId===m.id} onClick={()=>handleDeletePaymentMethod(m.id)}>
                      {pmDeletingId===m.id
                        ? <span className="sp-spinner" style={{borderTopColor:"#ef4444"}}/>
                        : <FiTrash2 style={{fontSize:12,color:"#ef4444"}}/>}
                    </button>
                  </div>
                ))}

            {showPmForm&&(
              <div className="pm-add-form">
                <div className="sp-section-title" style={{marginBottom:10}}>
                  <FiPlus style={{fontSize:11}}/> Novo método de pagamento
                </div>
                <div className="sp-grid-2" style={{marginBottom:8}}>
                  <div>
                    <div className="sp-label">ID Vendus</div>
                    <input className="sp-input" type="number" placeholder="Ex: 113682249" value={pmNewId} onChange={e=>setPmNewId(e.target.value)}/>
                  </div>
                  <div>
                    <div className="sp-label">Nome</div>
                    <input className="sp-input" placeholder="Ex: Dinheiro" value={pmNewName} onChange={e=>setPmNewName(e.target.value)}/>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                  <Toggle checked={pmNewDefault} onChange={setPmNewDefault}/>
                  <span style={{fontSize:".78rem",fontWeight:700}}>Definir como padrão</span>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button className="sp-btn primary" onClick={handleAddPaymentMethod} disabled={pmAdding}>
                    {pmAdding?<><span className="sp-spinner" style={{borderTopColor:"#fff"}}/> A adicionar…</>:<><FiCheck style={{fontSize:12}}/> Guardar</>}
                  </button>
                  <button className="sp-btn" onClick={()=>{setShowPmForm(false);setPmNewId("");setPmNewName("");setPmNewDefault(false);}}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            <div className="sp-divider"/>
            <div className="sp-alert info" style={{marginBottom:0}}>
              <FiZap style={{flexShrink:0,marginTop:1}}/>
              <div>
                <strong>IDs padrão Eduall Angola:</strong><br/>
                Dinheiro: 113682249 · Transferência: 113682253 · Depósito: 113682251 · TPA: 113682250
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  /* ─────────────────────────────────────
     MAIN RENDER
  ───────────────────────────────────── */
  const anyBusy = loading || saving;

  return (
    <>
      <style>{css}</style>
      <div onClick={()=>setShow(true)} style={{display:"inline-block"}}>
        {toggle_btn||<button className="sp-btn primary"><FiPlus style={{fontSize:14}}/> Nova Proposta</button>}
      </div>

      <Modal show={show} onHide={()=>!anyBusy&&setShow(false)} centered className="sp-modal" backdrop="static">
        <Modal.Body>
          <div className="sp-layout">

            {/* ── Sidebar ── */}
            <div className="sp-sidebar">
              <div className="sp-sidebar-logo">
                <FiFileText style={{fontSize:15}}/> Proposta Comercial
              </div>

              <button className={`sp-nav ${tab==="form"?"active":""}`} onClick={()=>setTab("form")}>
                <FiList style={{fontSize:14,flexShrink:0}}/> Calculadora
              </button>

              <button className={`sp-nav ${tab==="docs"?"active":""}`} onClick={()=>setTab("docs")}>
                <PiFilePdf style={{fontSize:14,flexShrink:0}}/> Documentos
                {docs.length>0&&<span className="sp-nav-badge">{docs.length}</span>}
              </button>

              <button className={`sp-nav ${tab==="vendus"?"active":""}`} onClick={()=>setTab("vendus")}>
                <FiSettings style={{fontSize:14,flexShrink:0}}/> Vendus
                {vndConfigured
                  ? <span className="sp-nav-badge ok">✓</span>
                  : <span className="sp-nav-badge warn">!</span>}
              </button>

              <div style={{marginTop:"auto",paddingTop:12,borderTop:"1px solid var(--border,#e2e8f0)"}}>
                {savedProposal
                  ? <div className="sp-saved-pill"><FiCheck style={{fontSize:11}}/> Guardada</div>
                  : proposalCode
                    ? <div style={{fontSize:".67rem",color:"var(--muted,#6b7280)",padding:"0 8px",wordBreak:"break-all"}}>{proposalCode}</div>
                    : null}
              </div>
            </div>

            {/* ── Content ── */}
            <div className="sp-content">
              <div className="sp-topbar">
                <div>
                  <div style={{fontWeight:900,fontSize:".94rem"}}>
                    {tab==="form"   ? "Calculadora Comercial EDUALL"
                    :tab==="docs"   ? "Documentos da Proposta"
                    :                 "Configuração Vendus"}
                  </div>
                  <div style={{fontSize:".72rem",color:"var(--muted,#6b7280)",fontWeight:600,marginTop:1}}>
                    {tab==="form"   ? "Configure planos, serviços e produtos"
                    :tab==="docs"   ? "Emita faturas, proformas, recibos e outros documentos Vendus"
                    :                 "API Vendus · Métodos de pagamento · Endpoints"}
                  </div>
                </div>
                <button className="sp-icon-btn" onClick={()=>!anyBusy&&setShow(false)}>
                  <FiX style={{fontSize:15}}/>
                </button>
              </div>

              <div className="sp-body">
                {tab==="form"   && renderForm()}
                {tab==="docs"   && renderDocs()}
                {tab==="vendus" && renderVendus()}
              </div>

              <div className="sp-footer">
                <button className="sp-btn" onClick={()=>!anyBusy&&setShow(false)}>Fechar</button>

                {tab==="form"&&(
                  <>
                    <button className="sp-btn" onClick={handleExportPdf} disabled={anyBusy}>
                      {loading
                        ? <><span className="sp-spinner"/> A gerar…</>
                        : <><FiDownload style={{fontSize:13}}/> PDF local</>}
                    </button>
                    <button className="sp-btn primary" onClick={handleSave} disabled={anyBusy}>
                      {saving
                        ? <><span className="sp-spinner" style={{borderTopColor:"#fff"}}/> A guardar…</>
                        : <><FiSave style={{fontSize:13}}/> Guardar proposta</>}
                    </button>
                  </>
                )}

                {tab==="docs"&&savedProposal&&(
                  <button className="sp-btn" onClick={()=>setTab("form")}>
                    <FiList style={{fontSize:13}}/> Ver calculadora
                  </button>
                )}

                {tab==="vendus"&&(
                  <button className="sp-btn" onClick={handleTestVendus} disabled={vndTesting}>
                    {vndTesting
                      ? <><span className="sp-spinner"/> A testar…</>
                      : <><FiZap style={{fontSize:13}}/> Testar API Vendus</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}